import React, { useEffect, useState } from 'react';
import { FaTrash } from 'react-icons/fa';
import { LuMinus, LuPlus } from 'react-icons/lu';
import NumberFormat from 'react-number-format';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Button, Checkbox, Dimmer, Form, Icon, Loader, Select } from 'semantic-ui-react';
import { AddressModel } from '../../entities/address.model';
import { ClientModel } from '../../entities/client.model';
import { CompanyModel } from '../../entities/company.model';
import store from '../../store';
import { DELIVERY_NOTE_MAX_LENGTH } from '../../constants/delivery';
import { useOrderQuote } from '../../utils/orderQuote';
import { formatDate, validatePhone } from '../../utils/shared.utils';
import AddressComponent from '../address/address.component';
import AreaFields from '../address/area-fields.component';
import CheckoutHeader from './checkout-header';
import './delivery.scss';

interface DeliveryProps {
  basketItems: any,
  client: ClientModel,
  settings: any,
  basketAddress: AddressModel,
  basketAddressValue: string,
  basketWarehouseUpdates: string,
}

const mapStateToProps = (state: any) => {
  let basketItems: any[] = [];
  let productMap = new Map<Number, any>();
  let basketWarehouseUpdates = "";

  if (state.products && state.basket && state.basket.quantityById) {
    state.products.forEach((p: any) => {
      let product = Object.assign({}, p)
      if (state.basket.quantityById[product.id] !== null && state.basket.quantityById[product.id] !== undefined) {
        // exists in the basket
        product['count'] = state.basket.quantityById[product.id];
        // push to list
        basketItems.push(product);
        productMap.set(product.id, product);
        basketWarehouseUpdates += `${product.id}=${product.warehouseCount}, `
      }
    });
  }

  return {
    basketItems: basketItems,
    client: state.client,
    settings: state.settings,
    deliveryAddress: state.basket.address,
    basketAddress: state.basket.address,
    basketAddressValue: state.basket.addressValue,
    basketWarehouseUpdates: basketWarehouseUpdates,
  }
}

/** An amount from the backend quote; "…" until the quote has arrived. */
function Money(props: { value: number | null | undefined, className?: string, style?: React.CSSProperties }) {
  if (props.value === undefined || props.value === null) {
    return <span>…</span>
  }
  return <NumberFormat displayType={'text'} value={props.value} suffix="₮" thousandSeparator={true} className={props.className} style={props.style} />
}

function DeliveryPage(props: DeliveryProps) {

  let history = useHistory()
  let scrolled = false;

  const deliveryDayMargins = [1, 3, 4];

  const [errorMsg, setErrorMsg] = useState("")
  const [addressError, setAddressError] = useState("")
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const [addressOptions, setAddressOptions] = React.useState<any[]>([])
  const [selectedAddressValue, setSelectedAddressValue] = React.useState<any>('')
  const [orderAddress, setOrderAddress] = React.useState<any>(new AddressModel())
  const [newAddress, setNewAddress] = React.useState(new AddressModel())
  const [saveAddress, setSaveAddress] = React.useState(false)

  const [deliveryDateMap, setDeliveryDatesMap] = React.useState<Map<Number, any>>(new Map())// {day margin group: selected day margin}
  const [deliveryDateOptionsMap, setDeliveryDateOptionsMap] = React.useState<Map<Number, any>>(new Map())
  const [deliveryItemsMaster, setDeliveryItemsMaster] = React.useState<any[]>([])
  const [showCustomPriceMap, setShowCustomPriceMap] = React.useState(new Map())
  const [customPriceMap, setCustomPriceMap] = React.useState(new Map())
  const [addressMap, setAddressMap] = React.useState(new Map())
  const [showCustomDeliveryPriceMap, setShowCustomDeliveryPriceMap] = React.useState(new Map())
  const [customDeliveryPriceMap, setCustomDeliveryPriceMap] = React.useState(new Map())
  const [showCompanyForm, setShowCompanyForm] = React.useState(false)
  const [companyFormData, setCompanyFormData] = React.useState(new CompanyModel())
  const [companyFormError, setCompanyFormError] = React.useState("")
  const [companyFormSuccess, setCompanyFormSuccess] = React.useState("")
  const [savingCompany, setSavingCompany] = React.useState(false)
  const [wantsExpress, setWantsExpress] = React.useState(false)
  // self pickup: no delivery address, no delivery fee, no express
  const [pickup, setPickup] = React.useState(false)
  const [deliveryNote, setDeliveryNote] = React.useState('')

  const normalizeCompanyId = (value: any): number | null => {
    if (value === undefined || value === null || value === '') {
      return null
    }
    const parsed = Number(value)
    return Number.isNaN(parsed) ? null : parsed
  }

  // Every amount on this page comes from the backend quote (chef-api orderPricing.js); the page does no price math.
  // That includes whether express is available for the address, what it costs, and the total.
  const { quote, failed: quoteFailed } = useOrderQuote(
    props.basketItems?.length > 0 && props.client?.id ? preparePayload(true) : null)
  const expressQuote = quote?.express
  const expressChosen = Boolean(expressQuote?.chosen)
  const quotedDelivery = (deliveryDay: number | null) => quote?.deliveries.find((d) => d.deliveryDay === deliveryDay)

  useEffect(() => {
    // express cannot stay selected once the address is one it does not cover, or the order is picked up
    if (wantsExpress && (pickup || (quote && !quote.express.available))) {
      setWantsExpress(false)
    }
  }, [wantsExpress, pickup, quote])

  useEffect(() => {
    setTimeout(() => {
      if (!scrolled) {
        scrolled = true;
        window.scrollTo(0, 0);
      }
    });
  }, [])

  useEffect(() => {
    setTimeout(() => {
      prepareItems()
    });
  }, [props.basketItems, deliveryDateMap])

  useEffect(() => {
    setTimeout(() => {
      prepareDeliveryOptions()
    });
  }, [props.settings, deliveryItemsMaster])

  async function fetchClients() {
    var token = localStorage.getItem('clientToken')
    if (token && token !== 'undefined') {
      fetch(`${process.env.REACT_APP_API_URL}client-list`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': token,
          'Content-Type': 'application/json'
        }
      }).then(res => {
        if (res.ok) {
          res.json().then((resJson: any) => {
            setErrorMsg("")
            let options = []
            let addressMap = new Map()
            for (let i = 0; i < resJson.length; i++) {
              let company = resJson[i]
              const companyId = normalizeCompanyId(company.id)
              if (companyId === null) {
                continue
              }
              let companyAddress = company.street || ''
              if (company.town || company.district || company.unit) {
                const addressParts = [company.town, company.district, company.unit, company.street].filter(Boolean)
                companyAddress = addressParts.join(', ')
              }
              options.push({
                key: companyId,
                value: companyId,
                text: company.name + (companyAddress ? ", Хаяг: " + companyAddress : "")
              })
              addressMap.set(companyId, {
                clubMember: company.clubMember,
                address: AddressModel.createAddressModel(company)
              })
            }
            setAddressOptions(options)
            setAddressMap(addressMap)
            const preferredCompanyId = normalizeCompanyId(props.basketAddressValue)
            if (preferredCompanyId !== null && addressMap.has(preferredCompanyId)) {
              setSelectedAddressValue(preferredCompanyId)
              setOrderAddress(AddressModel.createAddressModel(addressMap.get(preferredCompanyId).address))
            } else if (options.length > 0) {
              setSelectedAddressValue(options[0].value)
              setOrderAddress(AddressModel.createAddressModel(addressMap.get(options[0].value).address))
            }
          })
        }
      }).catch(err => {
        setErrorMsg("Сүлжээний алдаа")
        console.error(err);
      })
    } else {
      console.debug("Will redirect to: " + window.location.pathname)
      store.dispatch({ type: "redirectUrl", data: window.location.pathname })
      history.push("/login")
    }
  }

  useEffect(() => {
    if (props.client.superUser === 1) {
      // fetch client list
      fetchClients();
    } else {
      let options = []
      // client address
      if (props?.client?.street && props?.client?.unit && props?.client?.district) {
        options.push({
          key: 0,
          value: 'clientAddress',
          text: `${props.client.town}, ${props.client.district}, ${props.client.unit}, ${props.client.street}`
        })
        // select client address by default
        if (!props.basketAddressValue || props.basketAddressValue === 'clientAddress') {
          setOrderAddress(AddressModel.createAddressModel(props.client))
          setSelectedAddressValue('clientAddress')
        }
      }
      // company address
      if (props.client?.company && props.client?.company?.id && props.client?.company?.street && props.client?.company?.unit && props.client?.company?.district) {
        if ((options.length === 0 && !props.basketAddressValue) || props.basketAddressValue === 'companyAddress') {
          // select company address by default
          setOrderAddress(AddressModel.createAddressModel(props.client.company))
          setSelectedAddressValue('companyAddress')
        }
        options.push({
          key: 1,
          value: 'companyAddress',
          text: `${props.client.company.town}, ${props.client.company.district}, ${props.client.company.unit}, ${props.client.company.street}`,
        })
      }
      setAddressOptions(options)

      // new address required
      if (options.length === 0) {
        setSelectedAddressValue('newAddress')
      }

    }
  }, [props.client])

  // Groups the basket by delivery day margin and by the delivery date picked for it. Prices are not worked out here.
  const prepareItems = () => {
    if (props?.basketItems?.length > 0) {
      let deliveryItems: any[] = [];
      let marginPosMap = new Map();

      for (let dayMargin of deliveryDayMargins) {
        deliveryItems.push({ dayMargin: dayMargin, products: [] })
        marginPosMap.set(dayMargin, deliveryItems.length - 1)
      }
      // find day margin group for each product
      for (let basketItem of props.basketItems) {
        // find delivery dates (default to 1 when margin missing so product goes to earliest group)
        const itemMargin = basketItem.deliveryDayMargin ?? basketItem.deliverDayMargin ?? 1;
        for (let ii = 0; ii < deliveryDayMargins.length; ii++) {
          let dMargin = deliveryDayMargins[ii]
          if (itemMargin <= dMargin) {
            // found group
            let postInMap = marginPosMap.get(dMargin)
            deliveryItems[postInMap].products.push(basketItem);
            break;
          }
          else if (dMargin < deliveryDayMargins[deliveryDayMargins.length - 1]) {
            // pass item to the next group
            continue;
          }
          else {
            // item falls to the last group (far delivery)
            let postInMap = marginPosMap.get(dMargin)
            if (itemMargin > dMargin) {
              // lengthen the delivery day because of the current item delivery margin
              deliveryItems[postInMap].dayMargin = itemMargin
            }
            deliveryItems[postInMap].products.push(basketItem)
            marginPosMap.set(itemMargin, postInMap)
            break;
          }
        }
      }

      // group day margins per selected delivery date
      let deliveryItemsMaster: any[] = [{ date: null, groups: [] }];
      let datePosMap = new Map();
      datePosMap.set(null, 0)

      for (let deliveryObj of deliveryItems) {
        if (deliveryObj.products.length > 0) {
          if (deliveryDateMap.has(deliveryObj.dayMargin)) {
            let date = deliveryDateMap.get(deliveryObj.dayMargin)
            if (datePosMap.has(date)) {
              deliveryItemsMaster[datePosMap.get(date)].groups.push(deliveryObj)
            }
            else {
              deliveryItemsMaster.push({ date: date, groups: [deliveryObj] })
              datePosMap.set(date, deliveryItemsMaster.length - 1)
            }
          }
          else {
            deliveryItemsMaster[0].groups.push(deliveryObj)
          }
        }
      }

      setDeliveryItemsMaster(deliveryItemsMaster)
    }
  }

  const prepareDeliveryOptions = () => {
    if (deliveryItemsMaster?.length === 0 || props.basketItems?.length === 0) {
      return;
    }
    if (!props.settings.deliveryMaxDays) {
      return;
    }

    let deliveryDateOptionsMap = new Map()

    // populate delivery date options
    let dayOffSet = new Set();
    (props.settings.dayOffs || []).forEach((item: any) => dayOffSet.add(formatDate(new Date(item.date))))
    for (let delGroup of deliveryItemsMaster) {
      for (let deliveryObj of delGroup.groups) {
        if (deliveryObj.products.length > 0) {
          deliveryDateOptionsMap.set(deliveryObj.dayMargin, { dateOptions: [] })
          // Use earliest delivery day among products (default 1 when margin missing)
          const productMargins = deliveryObj.products.map((p: any) => p.deliveryDayMargin ?? p.deliverDayMargin ?? 1);
          let minMargin = Math.min(...productMargins);
          let deliveryDate = new Date();
          deliveryDate.setDate(deliveryDate.getDate() + minMargin);
          let dateOptions = []
          let daysFromToday = minMargin;
          for (let ii = 0; ii < props.settings.deliveryMaxDays; ii++) {
            if (dayOffSet.has(formatDate(deliveryDate))) {
              deliveryDate.setDate(deliveryDate.getDate() + 1);
              daysFromToday++;
              continue;
            }
            let dateOption = {
              key: `margin-${deliveryObj.dayMargin}-${daysFromToday}`,
              value: daysFromToday,
              text: `${deliveryDate.getMonth() + 1} сарын ${deliveryDate.getDate()}`,
            };
            dateOptions.push(dateOption)
            deliveryDate.setDate(deliveryDate.getDate() + 1);
            daysFromToday++;
          }
          deliveryDateOptionsMap.get(deliveryObj.dayMargin).dateOptions = dateOptions;
        }
      }
    }
    setDeliveryDateOptionsMap(deliveryDateOptionsMap)
  }

  const continueAction = () => {
    if (props?.client?.id) {
      // validate delivery dates
      setSubmitted(true)
      for (let delGroup of deliveryItemsMaster) {
        for (let deliveryObj of delGroup.groups) {
          if (!deliveryDateMap.has(deliveryObj.dayMargin)) {
            setErrorMsg(`Хүргэх өдрийг сонгоно уу: ${deliveryObj.products.map((p: any) => p.name).join(", ")}`)
            return;
          }
        }
      }
      // validate address
      if (props.client.superUser !== 1 && !pickup && (!orderAddress.town || !orderAddress.district || !orderAddress.unit || !orderAddress.street)) {
        setAddressError("Та хүргэх хаяг оруулна уу");
        return;
      }
      // submit order
      submitOrder();
    }
  }

  /**
   * The order-new payload, which is also what is sent to the backend for a price quote (`forQuote`: the same
   * numbers minus the parts that do not change the price, so typing a note does not request a new quote).
   * Prices are never part of it, except what a super user typed in by hand.
   */
  function preparePayload(forQuote = false) {
    let items: any[] = [];

    for (let delGroup of deliveryItemsMaster) {
      for (let deliveryObj of delGroup.groups) {
        for (let basketItem of deliveryObj.products) {
          if (basketItem.count) {
            let item = {
              productId: basketItem.id,
              count: basketItem.count,
              deliveryDay: delGroup.date,
              customPrice: 0
            }
            if (props.client.superUser === 1 && customPriceMap.has(basketItem.id)) {
              item.customPrice = customPriceMap.get(basketItem.id)
            }
            items.push(item)
          }
        }
      }
    }

    // super user: only the delivery prices typed in by hand; the backend works out all the others
    const deliveries = props.client.superUser === 1
      ? Array.from(customDeliveryPriceMap.entries()).map(([deliveryDay, price]) => ({
        deliveryDay: deliveryDay,
        customDeliveryPrice: Number(price),
      }))
      : []

    return {
      companyId: props.client?.superUser === 1
        ? normalizeCompanyId(selectedAddressValue)
        : normalizeCompanyId(props.client?.company?.id),
      products: items,
      deliveries: deliveries,
      // a pickup order has no delivery address; otherwise the picked khoroo goes by id (null: a free-text address)
      address: pickup ? undefined : orderAddress,
      deliveryAreaId: pickup ? undefined : (orderAddress.deliveryAreaId ?? null),
      express: wantsExpress && !pickup,
      pickup: pickup,
      ...(forQuote ? {} : {
        saveAddress: pickup ? false : saveAddress,
        deliveryNote: deliveryNote.trim() || undefined,
      }),
    };
  }
  const clientStoreFetch = (token: string) => {
    if (saveAddress && token) {
      fetch(`${process.env.REACT_APP_API_URL}client`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': token,
          'Content-Type': 'application/json'
        }
      }).then(clientRes => {
        if (clientRes.ok) {
          clientRes.json().then((clientJson: any) => {
            store.dispatch({
              type: 'authenticateClient',
              client: clientJson.client
            })
          })
        }
      }).catch(err => {
        console.error('Failed to refresh client info:', err)
      })
    }
  }
  const submitOrder = () => {
    var token = localStorage.getItem('clientToken')
    if (token && token !== 'undefined') {
      setErrorMsg("")
      setAddressError("")
      let payload = preparePayload();
      console.debug("payload", payload)
      setSubmitting(true)
      fetch(`${process.env.REACT_APP_API_URL}client/order-new`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Authorization': token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      }).then(res => {
        setSubmitting(false)
        if (res.ok) {
          res.json().then((resJson: any) => {
            clientStoreFetch(token || '');
            setErrorMsg("")
            setAddressError("")
            store.dispatch({
              type: 'clearBasket'
            })
            store.dispatch({ type: 'REFRESH_PRODUCTS' })
            if (resJson.status === 'PLACED') {
              history.push("/client/payment/" + resJson.token)
            }
            else if (resJson.status === 'CONFIRMED') {
              history.push("/client/order/success/" + resJson.token)
            }
          })
        }
        else if (res.status === 409) {
          // not enough stock
          res.json().then((resJson: any) => {
            if (resJson.errors) {
              let errorMsg = "Үлдэгдэл хүрэлцэхгүй бүтээгдэхүүн: "
              for (let stockErr of resJson.errors) {
                errorMsg += `${stockErr.name}, `
              }
              errorMsg = errorMsg.substring(0, errorMsg.length - 2)
              setErrorMsg(errorMsg)
            }
          })
        }
        else {
          res.json().then((resJson: any) => {
            if (resJson && resJson.error) {
              setErrorMsg(resJson.error);
            }
            else {
              setErrorMsg("Серверт алдаа гарлаа")
            }
          })
        }
      }).catch(err => {
        setSubmitting(false)
        setErrorMsg("Сүлжээний алдаа")
        console.error(err);
      })
    }
    else {
      console.debug("Will redirect to: " + window.location.pathname)
      store.dispatch({ type: "redirectUrl", data: window.location.pathname })
      history.push("/login")
    }
  }

  const onHeaderClick = (index: any) => {
  }

  const onCustomPriceChange = (product: any, e: any) => {
    if (!e.target.value || e.target.value === "0") {
      return;
    }
    let customPrice: number = Number(e.target.value);
    let tempMap = new Map(customPriceMap);
    tempMap.set(product.id, customPrice);
    setCustomPriceMap(tempMap);
  }

  const toggleCustomPrice = (product: any) => {
    let tempMap = new Map(showCustomPriceMap);
    if (showCustomPriceMap.has(product.id)) {
      tempMap.delete(product.id)
    } else {
      tempMap.set(product.id, true)
    }
    setShowCustomPriceMap(tempMap);
  }

  const toggleCustomDeliveryPrice = (deliveryDate: number | null, currentCost: number) => {
    if (deliveryDate === undefined || deliveryDate === null) {
      return;
    }
    let tempMap = new Map(showCustomDeliveryPriceMap);
    if (tempMap.has(deliveryDate)) {
      tempMap.delete(deliveryDate);
    } else {
      tempMap.set(deliveryDate, true);
      if (!customDeliveryPriceMap.has(deliveryDate)) {
        let deliveryPriceMap = new Map(customDeliveryPriceMap);
        deliveryPriceMap.set(deliveryDate, currentCost);
        setCustomDeliveryPriceMap(deliveryPriceMap);
      }
    }
    setShowCustomDeliveryPriceMap(tempMap);
  }

  const onCustomDeliveryPriceChange = (deliveryDate: number, e: any) => {
    if (deliveryDate === undefined || deliveryDate === null) {
      return;
    }
    let customPrice = Number(e.target.value);
    if (isNaN(customPrice)) {
      customPrice = 0;
    }
    let tempMap = new Map(customDeliveryPriceMap);
    tempMap.set(deliveryDate, customPrice);
    setCustomDeliveryPriceMap(tempMap);
  }

  const onItemCountChange = (product: any, e: any) => {
    if (!e.target.value || e.target.value === "0") {
      store.dispatch({
        type: 'updateBasketItemCount',
        product: product,
        count: "0"
      })
      return;
    }
    let count: number = Number(e.target.value);
    if (count > product.warehouseCount) {
      return;
    }
    store.dispatch({
      type: 'updateBasketItemCount',
      product: product,
      count: count
    })
  }

  const itemIncreaseCount = (product: any) => {
    let count = product.count;
    if (count >= product.warehouseCount) {
      return;
    }
    count += 1;
    store.dispatch({
      type: 'updateBasketItemCount',
      product: product,
      count: count
    })
  }

  const itemDecreaseCount = (product: any) => {
    let count = product.count;
    if (count < 1) {
      count = 0;
    }
    else {
      count -= 1;
    }
    store.dispatch({
      type: 'updateBasketItemCount',
      product: product,
      count: count
    })
  }

  const onItemCountBlur = (product: any, e: any) => {
    if (!e.target.value || e.target?.value === "0") {
      store.dispatch({
        type: 'removeItemFromBasket',
        product: product
      })
    }
  }

  const removeItemFromBasket = (product: any) => {
    store.dispatch({
      type: 'removeItemFromBasket',
      product: product
    });
  }

  const onDeliveryDateChange = (dayMargin: number, data: any) => {
    if (dayMargin === undefined || dayMargin === null) {
      return;
    }
    let deliveryDates = new Map(deliveryDateMap)
    deliveryDates.set(dayMargin, data.value);
    setDeliveryDatesMap(deliveryDates)
  }

  const onAddressChange = (e: any, data: any) => {
    setSelectedAddressValue(data.value)
    setCustomPriceMap(new Map())
    for (let addr of addressOptions) {
      if (addr.value === data.value) {
        let address;
        if (props.client.superUser === 1) {
          address = AddressModel.createAddressModel(addressMap.get(data.value).address)
          store.dispatch({
            type: 'basketAddress',
            data: address,
            value: data.value
          })
        } else {
          address = AddressModel.createAddressModel((data.value === 'clientAddress') ? props.client : props.client.company)
          store.dispatch({
            type: 'basketAddress',
            data: address,
            value: data.value
          })
        }
        setOrderAddress(address)
        break;
      }
    }
  }

  const onNewAddressChange = (address: AddressModel) => {
    setNewAddress(address)
    // the address is the picked khoroo (by id) plus the street; until both are there the order has no address yet
    setOrderAddress(address)
    if (address.deliveryAreaId && address.street) {
      store.dispatch({
        type: 'basketAddress',
        data: address,
        value: 'newAddress'
      })
      setAddressError("")
    }
  }

  const onAddressSaveChange = (e: any) => {
    setSaveAddress(e.target.checked)
  }

  const toggleCompanyForm = () => {
    setShowCompanyForm(!showCompanyForm)
    if (showCompanyForm) {
      // Reset form when hiding
      setCompanyFormData(new CompanyModel())
      setCompanyFormError("")
      setCompanyFormSuccess("")
    }
  }

  const onCompanyNameChange = (e: any) => {
    setCompanyFormData({ ...companyFormData, name: e.target.value })
  }

  const onCompanyRegisterChange = (e: any) => {
    setCompanyFormData({ ...companyFormData, register: e.target.value })
  }

  const onCompanyStreetChange = (e: any) => {
    setCompanyFormData({ ...companyFormData, street: e.target.value })
  }

  const onCompanyPhoneChange = (e: any) => {
    setCompanyFormData({ ...companyFormData, phone: e.target.value })
  }

  const validateCompanyForm = (): boolean => {
    setCompanyFormSuccess("")
    if (!companyFormData.name) {
      setCompanyFormError('Байгууллагын нэрийг оруулна уу.')
      return false
    }
    if (!companyFormData.register) {
      setCompanyFormError('Байгууллагын регистр оруулна уу.')
      return false
    }
    if (!companyFormData.phone) {
      setCompanyFormError('Байгууллагын утасны дугаар оруулна уу.')
      return false
    }
    if (!validatePhone(companyFormData.phone)) {
      setCompanyFormError('Байгууллагын утасны дугаараа шалгаж үзнэ үү.')
      return false
    }
    if (!companyFormData.town) {
      setCompanyFormError('Хот сонгоно уу')
      return false
    }
    if (!companyFormData.district) {
      setCompanyFormError('Дүүрэг сонгоно уу')
      return false
    }
    if (!companyFormData.deliveryAreaId) {
      setCompanyFormError('Хороо сонгоно уу')
      return false
    }
    if (!companyFormData.street) {
      setCompanyFormError('Дэлгэрэнгүй хаяг оруулна уу')
      return false
    }
    setCompanyFormError("")
    return true
  }

  const saveCompany = async () => {
    if (!validateCompanyForm()) {
      window.scrollTo(0, 0)
      return
    }

    const token = localStorage.getItem('clientToken')
    if (!token || token === 'undefined') {
      setCompanyFormError('Нэвтэрнэ үү')
      window.scrollTo(0, 0)
      return
    }

    setSavingCompany(true)
    setCompanyFormError("")

    const payload = {
      name: companyFormData.name,
      register: companyFormData.register,
      phone: companyFormData.phone,
      // the address is saved by the picked khoroo's id; the backend fills in the town / district / unit text
      deliveryAreaId: companyFormData.deliveryAreaId,
      street: companyFormData.street
    }

    try {
      const res = await fetch(process.env.REACT_APP_API_URL + 'client/company/new', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          Authorization: token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })

      if (res.status === 200) {
        setSavingCompany(false)

        try {
          // Check if response has content before parsing
          const contentType = res.headers.get('content-type')
          let newCompany: any = null

          if (contentType && contentType.includes('application/json')) {
            const text = await res.text()
            if (text) {
              newCompany = JSON.parse(text)
            }
          }

          // If no company data returned, create from form data
          if (!newCompany || !newCompany.id) {
            newCompany = {
              id: Date.now(), // Temporary ID, will be replaced after refetch
              name: companyFormData.name,
              register: companyFormData.register,
              phone: companyFormData.phone,
              town: companyFormData.town,
              district: companyFormData.district,
              unit: companyFormData.unit,
              deliveryAreaId: companyFormData.deliveryAreaId,
              street: companyFormData.street,
            }
          }

          // For super users, refetch the client list to ensure consistency
          if (props.client.superUser === 1) {
            // Refetch clients and then select the new company
            const token = localStorage.getItem('clientToken')
            if (token && token !== 'undefined') {
              fetch(`${process.env.REACT_APP_API_URL}client-list`, {
                method: 'GET',
                headers: {
                  'Accept': 'application/json',
                  'Authorization': token,
                  'Content-Type': 'application/json'
                }
              }).then(res => {
                if (res.ok) {
                  res.json().then((companies: any[]) => {
                    let options = []
                    let addressMap = new Map()
                    for (let i = 0; i < companies.length; i++) {
                      let company = companies[i]
                      const companyId = normalizeCompanyId(company.id)
                      if (companyId === null) {
                        continue
                      }
                      let companyAddress = company.street || ''
                      if (company.town || company.district || company.unit) {
                        const addressParts = [company.town, company.district, company.unit, company.street].filter(Boolean)
                        companyAddress = addressParts.join(', ')
                      }
                      options.push({
                        key: companyId,
                        value: companyId,
                        text: company.name + (companyAddress ? ", Хаяг: " + companyAddress : "")
                      })
                      addressMap.set(companyId, {
                        clubMember: company.clubMember,
                        address: AddressModel.createAddressModel(company)
                      })
                    }
                    setAddressOptions(options)
                    setAddressMap(addressMap)

                    // Select the new company (use the last one if ID not found)
                    const savedCompany = companies.find(c => c.name === companyFormData.name && c.register === companyFormData.register) || companies[companies.length - 1]
                    if (savedCompany) {
                      const savedCompanyId = normalizeCompanyId(savedCompany.id)
                      if (savedCompanyId !== null) {
                        setSelectedAddressValue(savedCompanyId)
                        setOrderAddress(AddressModel.createAddressModel(savedCompany))
                      }
                    }
                  }).catch(err => {
                    console.error('Error parsing companies list:', err)
                    setCompanyFormError('Компани хадгалагдсан боловч жагсаалтыг шинэчлэхэд алдаа гарлаа')
                  })
                } else {
                  setCompanyFormError('Компани хадгалагдсан боловч жагсаалтыг шинэчлэхэд алдаа гарлаа')
                }
              }).catch(err => {
                console.error('Error fetching companies list:', err)
                setCompanyFormError('Компани хадгалагдсан боловч жагсаалтыг шинэчлэхэд алдаа гарлаа')
              })
            }
          } else {
            // For regular users, add the company directly to options
            let companyAddress = newCompany.street || ''
            if (newCompany.town || newCompany.district || newCompany.unit) {
              const addressParts = [newCompany.town, newCompany.district, newCompany.unit, newCompany.street].filter(Boolean)
              companyAddress = addressParts.join(', ')
            }
            const newOption = {
              key: newCompany.id,
              value: newCompany.id,
              text: newCompany.name + (companyAddress ? ", Хаяг: " + companyAddress : "")
            }

            const updatedOptions = [...addressOptions, newOption]
            setAddressOptions(updatedOptions)

            const updatedAddressMap = new Map(addressMap)
            updatedAddressMap.set(newCompany.id, {
              address: AddressModel.createAddressModel(newCompany)
            })
            setAddressMap(updatedAddressMap)

            // Select the new company
            setSelectedAddressValue(newCompany.id)
            setOrderAddress(AddressModel.createAddressModel(newCompany))
          }

          // Reset form
          setCompanyFormData(new CompanyModel())
          setCompanyFormSuccess('Амжилттай хадгаллаа.')
          setCompanyFormError("")
          setShowCompanyForm(false)

          window.scrollTo(0, 0)
        } catch (parseError) {
          console.error('Error parsing response:', parseError)
          setCompanyFormError('Хадгалсан боловч мэдээлэл авахад алдаа гарлаа')
          setSavingCompany(false)
        }
      } else if (res.status === 401) {
        setSavingCompany(false)
        setCompanyFormError('Нэвтэрнэ үү')
        window.scrollTo(0, 0)
      } else {
        const resJson = await res.json()
        setSavingCompany(false)
        setCompanyFormError(resJson.error || resJson.errorMsg || 'Хадгалахад алдаа гарлаа')
        window.scrollTo(0, 0)
      }
    } catch (err) {
      setSavingCompany(false)
      setCompanyFormError('Сүлжээний алдаа')
      console.error(err)
      window.scrollTo(0, 0)
    }
  }

  return (
    <div className="page checkout-page">
      <Dimmer active={submitting}>
        <Loader size='medium'></Loader>
      </Dimmer>
      <div className="checkout-container">
        <div className="checkout-content">
          <CheckoutHeader activeIndex={2} onClick={onHeaderClick}></CheckoutHeader>

          <div className="delivery-info">
            <Icon name="info circle" />
            Нэг өдөр хүргэх нийт барааны үнэ <NumberFormat displayType={'text'} value={props.settings.freeDeliveryMinAmount} suffix="₮" thousandSeparator={true} />-с дээш бол үнэгүй хүргэнэ.
          </div>

          <div className="delivery-method-section" data-testid="checkout-method">
            <h2>Хүлээн авах хэлбэр</h2>
            <div className="delivery-method-options">
              <Checkbox
                radio
                data-testid="checkout-method-delivery"
                label="Хүргүүлэх"
                checked={!pickup}
                onChange={() => setPickup(false)}
              />
              <Checkbox
                radio
                data-testid="checkout-method-pickup"
                label="Очиж авах"
                checked={pickup}
                onChange={() => setPickup(true)}
              />
            </div>
          </div>

          {pickup ? (
            <div className="pickup-section" data-testid="checkout-pickup-info">
              <strong>Очиж авах</strong>: хүргэлтийн үнэ тооцогдохгүй (0₮).
              {props.settings.address ? <div>Авах хаяг: <strong>{props.settings.address}</strong></div> : null}
              {props.settings.supportPhone ? <div>Холбоо барих утас: <strong>{props.settings.supportPhone}</strong></div> : null}
            </div>
          ) : null}

          {/* a super user still picks the ordering company, so only the plain customer's address goes away */}
          {!pickup || props.client.superUser === 1 ? (
            <div className="address-section">
              <div className="address-section-inner">
                <h2>{props.client.superUser === 1 ? "Захиалагч байгууллага" : "Хүргэх хаяг"}</h2>
                {addressError ? <div className="error-msg">{addressError}</div> : null}

                {addressOptions.length > 0 ?
                  <div className="address-select-container">
                    <Select placeholder='Хаяг'
                      search
                      fluid
                      className='address-select'
                      options={addressOptions}
                      value={selectedAddressValue}
                      onChange={onAddressChange} />
                  </div>
                  :
                  <React.Fragment>
                    <AddressComponent address={newAddress} onChange={onNewAddressChange}></AddressComponent>
                    <div className="filter-body ui checkbox">
                      <input type="checkbox" id="save-address" onChange={(e) => onAddressSaveChange(e)} checked={saveAddress} />
                      <label htmlFor="save-address">Хаяг хадгалах</label>
                    </div>
                  </React.Fragment>
                }
              </div>
            </div>
          ) : null}
          {!pickup ? (
            <div className="express-section" data-testid="checkout-express">
              <Checkbox
                data-testid="checkout-express-checkbox"
                label="Express хүргэлт"
                checked={expressChosen}
                disabled={!expressQuote?.available}
                onChange={(e, data) => setWantsExpress(Boolean(data.checked))}
              />
              {expressQuote?.available ? (
                <div className="express-info" data-testid="checkout-express-info">
                  Бүс: <strong>{expressQuote.zoneName}</strong>{' · '}
                  {expressQuote.free
                    ? <strong>Үнэгүй</strong>
                    : <strong><Money value={expressQuote.fee} /></strong>}
                  {expressQuote.freeMinAmount > 0 ? (
                    <span className="express-note">
                      {' '}(Захиалгын дүн <Money value={expressQuote.freeMinAmount} />-с дээш бол үнэгүй)
                    </span>
                  ) : null}
                  <div className="express-note">Express сонговол энгийн хүргэлтийн үнэ нэмэгдэхгүй, зөвхөн express үнэ төлнө.</div>
                </div>
              ) : (
                <div className="express-info express-unavailable" data-testid="checkout-express-unavailable">
                  {!quote ? '' : (orderAddress.district && orderAddress.unit
                    ? 'Энэ хаягт express хүргэлт хийх боломжгүй байна.'
                    : 'Хүргэх хаягаа сонгосны дараа express хүргэлт сонгох боломжтой.')}
                </div>
              )}
            </div>
          ) : null}
          <div className="add-company-section">
            {props.client.superUser === 1 ? <Button onClick={toggleCompanyForm}>Байгууллага +</Button> : null}
            {showCompanyForm && (
              <div className="add-company-form">
                <Form widths="equal">
                  <Form.Group>
                    {companyFormSuccess ? (
                      <div className="success-msg">{companyFormSuccess}</div>
                    ) : null}
                    {companyFormError ? (
                      <div className="error-msg">{companyFormError}</div>
                    ) : null}
                  </Form.Group>
                  <Form.Group widths="equal">
                    <Form.Field
                      data-testid="checkout-company-name"
                      label="Байгууллагын нэр"
                      required
                      control="input"
                      value={companyFormData.name}
                      onChange={onCompanyNameChange}
                    />
                  </Form.Group>
                  <Form.Group widths="equal">
                    <Form.Field
                      data-testid="checkout-company-register"
                      label="Байгууллагын регистр"
                      required
                      control="input"
                      value={companyFormData.register}
                      onChange={onCompanyRegisterChange}
                    />
                  </Form.Group>
                  <Form.Group widths="equal">
                    <Form.Field
                      data-testid="checkout-company-phone"
                      label="Утас"
                      required
                      control="input"
                      type="tel"
                      value={companyFormData.phone}
                      onChange={onCompanyPhoneChange}
                    />
                  </Form.Group>
                  <Form.Group widths="equal">
                    <Form.Field>
                      <AreaFields
                        testIdPrefix="checkout-company"
                        areaId={companyFormData.deliveryAreaId}
                        town={companyFormData.town}
                        district={companyFormData.district}
                        onChange={(area) => setCompanyFormData({
                          ...companyFormData,
                          deliveryAreaId: area.areaId,
                          town: area.town,
                          district: area.district,
                          unit: area.unit,
                        })}
                      />
                    </Form.Field>
                  </Form.Group>
                  <Form.Group widths="equal">
                    <Form.TextArea
                      data-testid="checkout-company-street"
                      label="Дэлгэрэнгүй хаяг"
                      required
                      value={companyFormData.street}
                      onChange={onCompanyStreetChange}
                    />
                  </Form.Group>
                  <br />
                  <Form.Button
                    data-testid="checkout-company-save"
                    disabled={savingCompany}
                    onClick={saveCompany}
                    color="red"
                  >
                    Хадгалах
                  </Form.Button>
                  <Button
                    onClick={toggleCompanyForm}
                    style={{ marginLeft: '10px' }}
                  >
                    Цуцлах
                  </Button>
                </Form>
              </div>
            )}
          </div>

          <div className="delivery-list">
            {deliveryItemsMaster.filter(d => d?.groups?.length > 0).map((delGroup: any, qindex: number) => {
              return <div className="products-table" key={"delivery-date-" + delGroup.date}>
                {delGroup.groups.map((deliveryObj: any, dindex: number) => {
                  return <div className="products-group" key={"day-margin-" + deliveryObj.dayMargin}>
                    {delGroup.date ? null : <div className={submitted && (deliveryDateMap.get(deliveryObj.dayMargin) === undefined || deliveryDateMap.get(deliveryObj.dayMargin) === null) ? "error-msg" : "warning-msg"}>
                      <Icon name="info circle" />Хүргэх өдөр сонгоно уу</div>}

                    <div className="products-row delivery-item">
                      <div className="ritem date-picker">
                        {/* <Icon name="calendar outline" size="large" color={submitted && (deliveryDateMap.get(deliveryObj.dayMargin) === undefined || deliveryDateMap.get(deliveryObj.dayMargin) === null) ? "red" : undefined} /> */}
                        <span className="date-container">
                          <Loader active={deliveryDateOptionsMap.get(deliveryObj.dayMargin)?.dateOptions?.length > 0 ? false : true} size="small" />
                          <Select data-testid={"checkout-delivery-date-" + deliveryObj.dayMargin} placeholder={deliveryDateOptionsMap.get(deliveryObj.dayMargin)?.dateOptions?.length > 0 ? (pickup ? 'Авах өдөр' : 'Хүргэх өдөр') : ''}
                            error={submitted && (deliveryDateMap.get(deliveryObj.dayMargin) === undefined || deliveryDateMap.get(deliveryObj.dayMargin) === null)}
                            options={deliveryDateOptionsMap?.get(deliveryObj.dayMargin)?.dateOptions || []}
                            value={delGroup.date}
                            fluid={window.innerWidth < 500}
                            disabled={deliveryDateOptionsMap?.get(deliveryObj.dayMargin)?.dateOptions?.length > 0 ? false : true}
                            onChange={(e: any, data: any) => onDeliveryDateChange(deliveryObj.dayMargin, data)}></Select>
                        </span>
                        <div className="delivery-time">
                          <Icon name="clock outline" />
                          {props?.settings?.deliveryTime}
                        </div>
                      </div>
                    </div>

                    {deliveryObj.products.map((product: any, pindex: number) => {
                      const quotedItem = quote?.items.find((item) => item.productId === product.id)
                      return <div className="products-row basket-item" key={"basket-item-" + pindex}>
                        <div className="ritem product-image" onClick={() => window.open("/product/" + product.slug)}>
                          <img width="40" height="40" src={product.imageXsUrl} />
                        </div>
                        <div className="ritem product-row-right flex-between">
                          <div className="ritem product-details ">
                            <div className={"product-name" + (product.warehouseCount < product.count ? " count-error" : "")}>
                              <div className="name" onClick={() => window.open("/product/" + product.slug)}>{product.name}</div>
                              <div className="price-per">
                                <span>{product.size && product.unit ? (product.size + " " + product.unit) : ""}</span>
                                <span className="remove" onClick={() => removeItemFromBasket(product)}>
                                  <FaTrash />&nbsp;устгах
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="ritem product-counts">
                            <div className="product-count">
                              <LuMinus size={30} className={product.warehouseCount < product.count ? "caret count-error" : "caret"} onClick={() => itemDecreaseCount(product)} />
                              <input type="number" value={product.count} min="0" className={product.warehouseCount < product.count ? "count-error" : ""}
                                onChange={(e) => onItemCountChange(product, e)}
                                onBlur={(e) => onItemCountBlur(product, e)} />
                              <LuPlus size={30} onClick={() => itemIncreaseCount(product)} className={product.warehouseCount < product.count ? "caret count-error" : "caret"} />
                            </div>
                            <div className="stock-line">{product.warehouseCount <= product.count ? ("Боломжит үлдэгдэл: " + product.warehouseCount) : ""}</div>
                          </div>
                          <div className="ritem product-price flex-between">
                            {showCustomPriceMap.has(product.id) ?
                              <input type="number" data-testid={"checkout-custom-price-input-" + product.id} value={customPriceMap.get(product.id)} className=""
                                onChange={(e) => onCustomPriceChange(product, e)}
                              />
                              :
                              <React.Fragment>
                                <span data-testid={"checkout-item-price-" + product.id}>
                                  <Money value={quotedItem?.price} />
                                </span>
                              </React.Fragment>
                            }
                            <span>&nbsp;x</span>
                            <span>&nbsp;{product.count}</span>
                            <span>&nbsp;=&nbsp;</span>
                            <span data-testid={"checkout-item-subtotal-" + product.id}>
                              <Money value={quotedItem?.lineTotal} />
                            </span>
                            {props?.client?.superUser === 1 ?
                              <Button data-testid={"checkout-price-change-toggle-" + product.id} className='btn-price-change' onClick={() => toggleCustomPrice(product)}>
                                {showCustomPriceMap.has(product.id) ? "✔️" : "Үнэ өөрчлөх"}
                              </Button> : null
                            }
                          </div>
                        </div>
                      </div>
                    })
                    }
                  </div>
                })}

                <div className="products-row delivery-total-amount strong-text">
                  <div className="ritem flex-14 text-right">Бүтээгдэхүүний үнэ:</div>
                  <div className="ritem text-right">
                    <Money value={delGroup.date ? quotedDelivery(delGroup.date)?.itemsTotal : quote?.undatedItemsTotal} />
                  </div>
                </div>

                {delGroup.date ? <div className="products-row delivery-total-amount strong-text">
                  <div className="ritem flex-14 text-right">Хүргэлтийн үнэ:</div>
                  {expressChosen ? (
                    <div className="ritem text-right" data-testid={`checkout-delivery-included-${delGroup.date}`}>Express-д багтсан</div>
                  ) : pickup ? (
                    <div className="ritem text-right" data-testid={`checkout-delivery-pickup-${delGroup.date}`}>Очиж авна · 0₮</div>
                  ) : (
                    <div className="ritem text-right delivery-price-editor">
                      {props?.client?.superUser === 1 ?
                        <React.Fragment>
                          {showCustomDeliveryPriceMap.has(delGroup.date) ?
                            <input type="number" value={customDeliveryPriceMap.has(delGroup.date) ? customDeliveryPriceMap.get(delGroup.date) : quotedDelivery(delGroup.date)?.cost} className=""
                              onChange={(e) => onCustomDeliveryPriceChange(delGroup.date, e)}
                            />
                            :
                            <Money value={quotedDelivery(delGroup.date)?.cost} />
                          }
                          <Button className='btn-price-change' onClick={() => toggleCustomDeliveryPrice(delGroup.date, quotedDelivery(delGroup.date)?.cost ?? 0)}>
                            {showCustomDeliveryPriceMap.has(delGroup.date) ? "✔️" : "Үнэ өөрчлөх"}
                          </Button>
                        </React.Fragment>
                        :
                        <Money value={quotedDelivery(delGroup.date)?.cost} />
                      }
                    </div>
                  )}
                </div> : null}
              </div>
            })}
          </div>

          {expressChosen && expressQuote ? (
            <div className="products-row delivery-total-amount strong-text" data-testid="checkout-express-line">
              <div className="ritem flex-14 text-right">Express хүргэлт ({expressQuote.zoneName}):</div>
              <div className="ritem text-right">
                {expressQuote.free ? 'Үнэгүй' : <Money value={expressQuote.fee} />}
              </div>
            </div>
          ) : null}

          {quoteFailed ? <div className="error-msg"><Icon name="warning circle" />Үнийг тооцоолж чадсангүй. Хуудсаа дахин ачаална уу.</div> : null}

          <div className="total-amount" data-testid="checkout-total">
            <span>Нийт: <Money value={quote?.total} /></span>
          </div>
        </div>

        <div className="delivery-note-section">
          <label htmlFor="checkout-delivery-note">
            {pickup ? 'Тайлбар' : 'Хүргэлтийн тайлбар'} <span className="delivery-note-optional">(заавал биш)</span>
          </label>
          <textarea
            id="checkout-delivery-note"
            data-testid="checkout-delivery-note"
            rows={3}
            maxLength={DELIVERY_NOTE_MAX_LENGTH}
            placeholder={pickup ? 'Жишээ: хэн ирж авах, хэзээ ирэх' : 'Жишээ: орцны код, хүргэхээс өмнө залгах'}
            value={deliveryNote}
            onChange={(e) => setDeliveryNote(e.target.value)}
          />
          <div className="delivery-note-counter">{deliveryNote.length}/{DELIVERY_NOTE_MAX_LENGTH}</div>
        </div>

        {errorMsg ? <div className="error-msg"><Icon name="warning circle" />{errorMsg}</div> : null}

        <div className="check-out-line">
          <Button data-testid="checkout-submit-button" className="width-100" color="red" onClick={() => continueAction()}
            disabled={submitting || props.basketItems?.length === 0}>Захиалах</Button>
        </div>
      </div>
    </div>
  )
}

export default connect(mapStateToProps)(DeliveryPage)